import { Modal } from "@circleco/compass/components/Modal";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { noop } from "lodash";
import { useEffect, useRef } from "react";

import { t } from "@/i18n-js/instance";

import { STEPS } from "../constants";
import { useImagePostModalStore } from "../store";
import { Carousel } from "./Carousel";
import { useUploadImages } from "./hooks/useUploadImages";
import type { AdjustImagesProps } from "./types";

export const AdjustImagesComponent = ({
  onClose,
  onCompleted = noop,
  isOnTop = false,
  editingImageSignedId,
  open = true,
}: AdjustImagesProps) => {
  const isMobile = useIsMobile();
  const handledStatusRef = useRef<string | null>(null);
  const { setCurrentStep, resetSelectedFiles } = useImagePostModalStore();
  const {
    uploadImages,
    overallProgress,
    resetUploads,
    status,
    fileStates,
    updateStoredImages,
  } = useUploadImages();

  const onModalClose = () => {
    resetSelectedFiles();
    onClose();
  };

  const onCancel = () => {
    resetSelectedFiles();
    if (isOnTop) {
      onClose();
    } else {
      setCurrentStep(STEPS.UPLOAD_IMAGES);
    }
  };

  useEffect(() => {
    if (status !== "completed" && status !== "error") {
      handledStatusRef.current = null;
      return;
    }

    if (handledStatusRef.current === status) {
      return;
    }

    handledStatusRef.current = status;

    if (status === "completed") {
      updateStoredImages(editingImageSignedId);
      resetUploads();
      resetSelectedFiles();
      setCurrentStep(STEPS.CREATE_POST);
      onCompleted();
      if (isOnTop) onClose();
    } else if (status === "error") {
      resetUploads();
    }
  }, [
    editingImageSignedId,
    isOnTop,
    onClose,
    onCompleted,
    resetSelectedFiles,
    resetUploads,
    setCurrentStep,
    status,
    updateStoredImages,
  ]);

  return (
    <Modal.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onModalClose();
      }}
      size={isMobile ? "full-screen" : "lg"}
    >
      <Modal.Content>
        <Modal.Header
          title={t("image_space.post_modal.steps.adjust_images.title")}
        />
        <Modal.Body customLayout>
          <div className="flex min-h-0 flex-1 flex-col">
            <Carousel
              status={status}
              fileStates={fileStates}
              overallProgress={overallProgress}
              editingImageSignedId={editingImageSignedId}
              onDeleteAllImages={onCancel}
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
            onClick: onCancel,
            shouldCloseModal: false,
          }}
          primaryAction={{
            label: isOnTop ? t("save") : t("next"),
            onClick: () => uploadImages(),
            disabled: status === "uploading",
            loading: status === "uploading",
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
